// src/components/SessionPanel/api.ts
import type { ChatMessage } from "./types";
import { trimTrailingSlash } from "../../lib/url";
import { API_BASE } from "../../config";

export async function postSummary(
  getToken: () => Promise<string | null>,
  _base: string, // Ignored - summary endpoint uses API_BASE
  session: string,
  text: string,
  pinned: string[],
  promptId?: string, // selected summarize prompt (prompt library); backend default when absent
): Promise<string> {
  const token = await getToken();
  if (!token) throw new Error("No token available for summary request");

  // Async tick/poll via HTTP API v2
  const postUrl = `${API_BASE}/summary?session=${encodeURIComponent(session)}`;
  const postResp = await fetch(postUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ session, text, pinned, ...(promptId ? { prompt_id: promptId } : {}) }),
  });
  if (!postResp.ok) {
    const txt = await postResp.text().catch(() => "");
    throw new Error(txt || `Summary request failed (${postResp.status})`);
  }
  const { run_id } = await postResp.json();
  if (!run_id) throw new Error("No run_id returned");

  // Poll for result
  const start = Date.now();
  const timeoutMs = 120_000;
  const intervalMs = 2000;
  return new Promise<string>((resolve, reject) => {
    const iv = setInterval(async () => {
      try {
        if (Date.now() - start > timeoutMs) {
          clearInterval(iv);
          reject(new Error("Timeout waiting for summary result"));
          return;
        }
        const freshToken = await getToken();
        if (!freshToken) {
          clearInterval(iv);
          reject(new Error("Authentication expired during polling"));
          return;
        }
        const pollUrl = `${API_BASE}/summary?run_id=${encodeURIComponent(run_id)}`;
        const r = await fetch(pollUrl, {
          headers: { Authorization: `Bearer ${freshToken}` },
        });
        if (!r.ok) {
          clearInterval(iv);
          reject(new Error(`Poll ${r.status}: ${await r.text().catch(() => "")}`));
          return;
        }
        const data = await r.json();
        if (data.status === "done") {
          clearInterval(iv);
          const parsed = typeof data.result === "string" ? JSON.parse(data.result) : data.result;
          const s = (parsed?.summary || "").trim();
          if (!s) reject(new Error("Empty summary returned."));
          else resolve(s);
        } else if (data.status === "error") {
          clearInterval(iv);
          reject(new Error(data.error || "Worker error"));
        }
      } catch (e) {
        clearInterval(iv);
        reject(e);
      }
    }, intervalMs);
  });
}

export async function postChat(
  authedFetch: (u: string, i?: RequestInit) => Promise<Response>,
  base: string,
  session: string,
  messages: ChatMessage[],
  context: { summary: string | null; pinned_quotes: string[]; recent_transcript: string[] },
): Promise<{ reply: string }> {
  const url = `${base}/chat`;
  const r = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session, messages, context }),
  });
  if (!r.ok) {
    const errText = await r.text().catch(() => "");
    throw new Error(errText || `Chat request failed (${r.status})`);
  }
  const data = await r.json().catch(() => ({ reply: "" } as { reply: string }));
  return data;
}

/**
 * Upload a JSON/JSONL transcript blob for an existing session.
 * Backend requires query params: session, patient_id, title, session_date
 * (session_date feeds the monthly session cap — the backend rejects
 * creates without it).
 * Send RAW BYTES (not multipart) so the Lambda can decode body directly.
 */
export async function uploadJson(
  authedFetch: (u: string, i?: RequestInit) => Promise<Response>,
  base: string,
  session: string,
  file: File,
  patientId: string,        // REQUIRED
  title: string,            // REQUIRED
  sessionDate: string,      // REQUIRED, "YYYY-MM-DD"
): Promise<any> {
  if (!session || session === "current") throw new Error("Invalid session id");
  if (!patientId) throw new Error("patientId is required");
  if (!title) throw new Error("title is required");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDate)) throw new Error("sessionDate (YYYY-MM-DD) is required");

  const baseUrl = trimTrailingSlash(base);
  const url =
    `${baseUrl}/upload/json` +
    `?session=${encodeURIComponent(session)}` +
    `&patient_id=${encodeURIComponent(patientId)}` +
    `&title=${encodeURIComponent(title)}` +
    `&session_date=${encodeURIComponent(sessionDate)}`;

  // send RAW BYTES
  const buf = await file.arrayBuffer();
  const resp = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream" },
    body: new Uint8Array(buf),
  });

  const text = await resp.text();
  let payload: any = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }

  if (!resp.ok) {
    throw new Error(
      `JSON upload failed: ${resp.status} :: ${typeof payload === "string" ? payload : JSON.stringify(payload)}`
    );
  }
  return payload;
}

export type SaveSessionBody = {
  patient_id?: string | null;
  therapist_id?: string | null;
  transcript?: string | null;
  summary?: string | null;
  lang?: string | null;
  duration_sec?: number | null;
  session_date?: string | null; // "YYYY-MM-DD"
};

/**
 * Save/Update a session via POST /sessions/save
 * Backend expects session_id in the BODY.
 * Only strip undefined/null; keep empty strings if caller wants to clear.
 */
export async function saveSession(
  authedFetch: (u: string, i?: RequestInit) => Promise<Response>,
  base: string,
  sessionId: string,
  body: Record<string, any>,
): Promise<any> {
  if (!sessionId || sessionId === "current") throw new Error("Invalid session id");

  const url = `${trimTrailingSlash(base)}/sessions/save`;

  const payload: Record<string, any> = { session_id: sessionId };
  for (const [k, v] of Object.entries(body)) {
    if (v !== undefined && v !== null) payload[k] = v;
  }

  const r = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const t = await r.text();
  if (!r.ok) {
    throw new Error(t || `Save session failed (${r.status})`);
  }
  const data = t ? JSON.parse(t) : {};
  return data;
}

/**
 * NEW: Delete a session via DELETE /sessions/{session_id}
 */
export async function deleteSession(
  authedFetch: (u: string, i?: RequestInit) => Promise<Response>,
  base: string,
  sessionId: string,
): Promise<{ ok: boolean; deleted_session_id?: string }> {
  if (!sessionId || sessionId === "current") throw new Error("Invalid session id");
  const url = `${trimTrailingSlash(base)}/sessions/${encodeURIComponent(sessionId)}`;
  const r = await authedFetch(url, { method: "DELETE" });
  const t = await r.text();
  if (!r.ok) {
    throw new Error(t || `Delete session failed (${r.status})`);
  }
  const data = t ? JSON.parse(t) : { ok: true };
  return data;
}
